/* eslint-disable i18next/no-literal-string */
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Card from '@/components/elements/atoms/Card/Card'
import Pill from '@/components/elements/atoms/Pill/Pill'
import Image from '@/components/shared/elements/Image/Image'
import Text from '@/components/shared/elements/Text/Text'

import STYLES from './TestimonialsTrustpilot.module.sass'

import { Code } from '@/shared_types/rails_models/shipping_countries'

import { cardContent, getInitials } from './helpers'

type Props = {
  shippingCountryCode: Code
  isPsibufet: boolean
}

type TabName =
  | 'category_1'
  | 'category_2'
  | 'category_3'
  | 'category_4'
  | 'category_5'

const TestimonialsTrustpilot = ({
  shippingCountryCode,
  isPsibufet
}: Props): React.ReactElement => {
  const tabs: TabName[] = [
    'category_1',
    'category_2',
    'category_3',
    'category_4',
    'category_5'
  ]

  const namespace = 'paid_traffic'
  const { t } = useTranslation(namespace)

  const [selectedTab, setSelectedTab] = useState<TabName>('category_1')

  const handleChange = useCallback(
    (e) => setSelectedTab(e.target.value),
    [setSelectedTab]
  )

  const tabOptions = tabs.map((tabName) => ({
    id: tabName,
    name: tabName,
    text: `phase_2.testimonials.categories.${tabName}.title`,
    value: tabName,
    checked: selectedTab === tabName,
    namespace,
    onChange: handleChange
  }))

  const filteredCategoryCards = cardContent(
    isPsibufet,
    shippingCountryCode
  ).filter((item) => item.category === selectedTab)

  return (
    <div className={STYLES.testimonialsTrustpilot}>
      <Text
        variant="textRegular20"
        bold
        namespace={namespace}
        align="center"
        text="phase_2.testimonials.category_title"
        colour="brandBlue500"
      />
      <div className={STYLES.pillsWrapper}>
        {tabOptions.map((tabOption) => (
          <Pill key={tabOption.id} {...tabOption} />
        ))}
      </div>
      <div className={STYLES.testimonialsCarousel}>
        {filteredCategoryCards.map((content) => {
          return (
            <Card className={STYLES.card} key={content.name} padding={20}>
              <div className={STYLES.avatarWrapper}>
                <div className={STYLES.avatar}>
                  {content.img ? (
                    <Image
                      alt={content.name}
                      slug={content.img}
                      image={{
                        height: 48,
                        width: 48,
                        resizeMode: 'resize_to_fit',
                        quality: 'auto:best',
                        retina: true
                      }}
                    />
                  ) : (
                    <Text
                      variant="textRegular14"
                      bold
                      colour="brandWhite"
                      translate={false}
                      namespace={namespace}
                      text={
                        content.name ===
                        'phase_2.testimonials.categories.category_3.cards.card_3.name'
                          ? getInitials(t(`${namespace}:${content.name}`))
                          : getInitials(content.name)
                      }
                    />
                  )}
                </div>
                <div className={STYLES.nameContainer}>
                  <Text
                    variant="textRegular16"
                    bold
                    namespace={namespace}
                    translate={
                      content.name ===
                      'phase_2.testimonials.categories.category_3.cards.card_3.name'
                    }
                    text={content.name}
                  />
                  <div className={STYLES.verified}>
                    <Image
                      alt=""
                      slug="checkmark"
                      image={{
                        height: 12,
                        width: 12
                      }}
                    />
                    <Text
                      variant="textRegular14"
                      namespace={namespace}
                      text="phase_2.testimonials.categories.verified"
                    />
                  </div>
                </div>
              </div>
              <div className={STYLES.contentWrapper}>
                <div className={STYLES.cardContent}>
                  <Image
                    alt={t(`phase_2.testimonials.categories.five_stars_alt`)}
                    slug="five-stars"
                    image={{
                      height: 20,
                      width: 109,
                      resizeMode: 'resize_to_fit',
                      quality: 'auto:best',
                      retina: true
                    }}
                  />
                  <div className={STYLES.cardTitle}>
                    <Text
                      element="span"
                      variant="textRegular20"
                      namespace={namespace}
                      text={`${content.copy}.title`}
                      bold
                    />
                  </div>
                  <Text
                    variant="textRegular16"
                    namespace={namespace}
                    text={`${content.copy}.quote`}
                  />
                </div>
                <a href={content.url} target="_blank" rel="noreferrer">
                  <Text
                    variant="textRegular14"
                    namespace={namespace}
                    colour="brandBlue400"
                    variables={{
                      date: content.date
                    }}
                    text="phase_2.testimonials.categories.card_footer"
                  />
                </a>
              </div>
            </Card>
          )
        })}
      </div>
    </div>
  )
}

export default TestimonialsTrustpilot

export { TabName }
